import { Component, h, State, Host } from '@stencil/core';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { timer } from 'rxjs';
import { takeWhile, tap } from 'rxjs/operators';
import spacetime from 'spacetime';

import { environment } from '../../tambr.config';
import { closeCheckout, checkoutLogout, openAddOnsModal, openCheckoutNoteModal, checkoutSuccess, checkoutError } from '../../modals/checkout.modal';
import { showPriceConfirmModal } from '../../modals/confirm.modal';
import { FirebaseService, paymentMethodConverter } from '../../utils/firebase';
import { formatDuration, formatOrderDate } from '../../utils/utils';
import { getPriceBreakdown } from '../../utils/order-price';
import { addCustomFieldToForm, getCustomFieldValues, getFormErrors } from '../../utils/form';

import { checkout } from '../../store/checkout.store';
import { settings } from '../../store/settings.store';
import { customer } from '../../store/customer.store';
import { schedule } from '../../store/schedule.store';
import { selection } from '../../store/selection.store';
import { orderFields, onOrderFieldsChange, startOrderFieldsListener, stopOrderFieldsListener } from '../../store/order-fields.store';

import { CheckoutStartRequest, CheckoutStartResponse, CheckoutSubmitRequest, CheckoutSubmitResponse } from '@artask/tambr-models/models/checkout.model';
import { OrderAddOn } from '@artask/tambr-models/models/add-on.model';
import { PlatformCustomerPaymentMethod } from '@artask/tambr-models/models/customer.model';
import { Space } from '@artask/tambr-models/models/spaces.model';
import { OrderInfoRow } from '../../utils/models';



@Component({
  tag: 'tambr-checkout-form',
  styleUrl: 'tambr-checkout-form.css',
  shadow: true
})
export class TambrCheckoutForm {
  
  @State() paymentMethodLoading: boolean = false;
  @State() paymentProcessing: boolean = false;
  @State() timerSeconds: number = 600;
  @State() termsConfirmed: boolean = settings.businessSettings?.checkoutSettings?.termsOfServiceEnabledByDefault;

  businessStripe: Stripe;
  paymentMethod: PlatformCustomerPaymentMethod = null;
  orderInfo: OrderInfoRow[] = [];

  paymentMethodAddEl: HTMLTambrPaymentMethodAddElement;
  paymentMethodSelectEl: HTMLTambrPaymentMethodSelectElement;
  messageEl: HTMLTambrMessageElement;

  componentWillLoad() {
    checkout.checkoutStartLoading = true;
    this.getOrderInfo();

    startOrderFieldsListener();
    
    onOrderFieldsChange( 'customFields', customFields => {
      for ( let customField of customFields ) {
        addCustomFieldToForm( checkout.customFieldsForm, customField );
      }
    });
  }

  async componentDidLoad() { 
    try {
      const checkoutStart = httpsCallable
        <CheckoutStartRequest, CheckoutStartResponse>
          ( FirebaseService.functions, 'checkout_start' );
          
      const checkoutStartResult = await checkoutStart({
        businessId: settings.businessId,
        date: selection.date,
        spaceId: selection.spaceId,
        increments: selection.increments,
        duration: selection.duration,
        spaceOptions: selection?.spaceOptions.map( s => ({ id: s.optionId, option: s.optionName, value: s.valueName }) )
      });

      console.log( 'checkoutStartResult', checkoutStartResult );

      if ( !checkoutStartResult.data.success ) {
        return checkoutError( checkoutStartResult.data.errorMessage, checkoutStartResult.data.errorTitle );
      }

      checkout.checkoutId = checkoutStartResult.data.checkoutId;

      this.businessStripe = await loadStripe(
        environment.stripePlatformId, { stripeAccount: settings.businessSettings.stripeAccount, apiVersion: '2022-11-15' }
      ); 

      this.loadDefaultPaymentMethod();
      this.startTimer();

      checkout.checkoutStartLoading = false;

    } catch(e) {
      console.error(e);
      checkout.checkoutStartLoading = false;
      return checkoutError( e.message );
    }    
  }

  disconnectedCallback() {
    this.timerSeconds = 0;
    stopOrderFieldsListener();
  }

  getOrderInfo() {
    const space = schedule.dateSchedule.spaces.find( (space: Space) => space.id === selection.spaceId ); 
    const location = schedule.dateSchedule?.locations?.find( location => location.id === space?.locationId );
    this.orderInfo.push({ label: 'Location', value: location?.title, cssClass: 'bold-value' });
    this.orderInfo.push({ label: 'Space', value: space.title, cssClass: 'bold-value' });
    this.orderInfo.push({ label: 'Date', value: formatOrderDate( selection.date ) });
    this.orderInfo.push({ label: 'Start time', value: spacetime( selection.increments[0] ).time() });
    this.orderInfo.push({ label: 'Duration', value: formatDuration( selection.duration ) });
    for ( const spaceOption of selection?.spaceOptions ) {
      this.orderInfo.push({ label: spaceOption.optionName, value: spaceOption.valueName });
    }
  }

  startTimer(){
    timer( 0, 1000 ).pipe( 
      takeWhile( () => this.timerSeconds > 0 ), 
      tap( () => this.timerSeconds-- ) 
    ).subscribe( () => {
      if ( this.timerSeconds === 0 ) {
        return checkoutError( 'Please start the checkout process again.', 'Checkout session has expired.' ); 
      }
    });
  }

  showTimer() {
    const settings = { minimumIntegerDigits: 2, useGrouping: false };
    const seconds = ( this.timerSeconds % 60 ).toLocaleString( 'en-US', settings );
    const minutes = Math.floor( this.timerSeconds / 60 ).toLocaleString( 'en-US', settings );
    return `${minutes}:${seconds}`;
  }

  handleTermsConfirm( event ) { 
    this.termsConfirmed = event.target.checked;
  }

  deselectAddOn( addOnId: string ) { 
    selection.addOns = selection.addOns.filter( selectedAddOn => selectedAddOn.id !== addOnId );
  }


  /**
   * Payment Methods
   */

  async loadDefaultPaymentMethod() {
    if ( !customer.platformCustomer?.stripeDefaultPaymentMethodId ) return;
    this.paymentMethodLoading = true;
    try {
      const paymentMethodPath = `users/${customer.platformCustomer.uid}/paymentMethods/${customer.platformCustomer.stripeDefaultPaymentMethodId}`;
      const paymentMethodDocRef = doc( FirebaseService.firestore, paymentMethodPath ).withConverter( paymentMethodConverter );
      const paymentMethodDoc = await getDoc( paymentMethodDocRef );
      if ( paymentMethodDoc.exists ) this.paymentMethod = { id: paymentMethodDoc.id, ...paymentMethodDoc.data() };
      console.log( 'loadDefaultPaymentMethod this.paymentMethod', this.paymentMethod );
      this.paymentMethodLoading = false;
      return;
    } catch (e) {
      console.error(e);
      this.paymentMethodLoading = false;
      return checkoutError( 'Error occurred while loading your default payment method. Please try again later.', 'Something went wrong', false ); 
    }    
  }

  async updateDefaultPaymentMethod( paymentMethod: PlatformCustomerPaymentMethod ) {
    if ( document.getElementsByTagName('tambr-payment-method-select').length ) this.paymentMethodSelectEl.remove();
    this.paymentMethodLoading = true;
    try {
      const userDocRef = doc( FirebaseService.firestore, `users/${customer.platformCustomer.uid}` );
      await updateDoc( userDocRef, { stripeDefaultPaymentMethodId: paymentMethod.id } );
      this.paymentMethod = paymentMethod;
      this.paymentMethodLoading = false;
      return;
    } catch(e) {
      console.error(e);
      this.paymentMethodLoading = false;
      return checkoutError( 'Error occurred while updating your default payment method. Please try again later.', 'Something went wrong', false ); 
    }
  }


  openPaymentMethodSelectDialog() {
    if ( document.getElementsByTagName('tambr-payment-method-select').length ) return;
    this.paymentMethodSelectEl = document.createElement('tambr-payment-method-select');
    this.paymentMethodSelectEl.userId = customer.platformCustomer.uid;
    this.paymentMethodSelectEl.initialPaymentMethodId = customer.platformCustomer.stripeDefaultPaymentMethodId;
    this.paymentMethodSelectEl.allowAdd = true;
    this.paymentMethodSelectEl.addEventListener( 'tambrSelectPaymentMethod', 
      (event: CustomEvent<PlatformCustomerPaymentMethod>) => this.updateDefaultPaymentMethod( event.detail )
    );
    this.paymentMethodSelectEl.addEventListener( 'tambrSelectPaymentMethodDialogClose', () => this.paymentMethodSelectEl.remove() );
    this.paymentMethodSelectEl.addEventListener( 'tambrAddNewPaymentMethod', () => {
      this.paymentMethodSelectEl.remove();
      this.openPaymentMethodAddDialog();
    });
    document.body.appendChild(this.paymentMethodSelectEl);
  }

  openPaymentMethodAddDialog() {
    if ( document.getElementsByTagName('tambr-payment-method-add').length ) return;    
    this.paymentMethodAddEl = document.createElement('tambr-payment-method-add');
    this.paymentMethodAddEl.addEventListener( 'tambrPaymentMethodAddSuccess', () => this.handlePaymentMethodAddSuccess() );
    this.paymentMethodAddEl.addEventListener( 'tambrPaymentMethodAddClose', () => this.closePaymentMethodAddDialog() );
    document.body.appendChild( this.paymentMethodAddEl );
  }

  handlePaymentMethodAddSuccess() {
    this.loadDefaultPaymentMethod();
    this.closePaymentMethodAddDialog();
  }

  closePaymentMethodAddDialog() {
    if ( document.getElementsByTagName('tambr-payment-method-add').length ) this.paymentMethodAddEl.remove();
  }


  async handleSubmit(ev: any, newTotal: number = null) {
    ev.preventDefault();
    checkout.customFieldsFormErrors = getFormErrors( checkout.customFieldsForm );
    if ( checkout.customFieldsFormErrors.length ) return;
    if ( this.paymentProcessing ) return;
    this.paymentProcessing = true;
    const { total } = getPriceBreakdown();

    try {      
      const checkoutSubmit = httpsCallable
        <CheckoutSubmitRequest, CheckoutSubmitResponse>
          ( FirebaseService.functions, 'checkout_submit' );

      const checkoutSubmitData: CheckoutSubmitRequest = {
        checkoutId: checkout.checkoutId,
        businessId: settings.businessId,
        selectedAddOns: selection.addOns,
        customerNote: selection.note,
        widgetCalculatedTotal: newTotal ?? total
      };

      const customFieldsValues = getCustomFieldValues( checkout.customFieldsForm, orderFields.customFields );

      if ( customFieldsValues.length ) {
        checkoutSubmitData.customFields = customFieldsValues;
      }

      const submitResponse = await checkoutSubmit( checkoutSubmitData );
      console.log( 'Payment intent submit response: ', submitResponse );

      // price === 0: no payment intent
      if ( submitResponse.data?.no_charge ) {        
        this.paymentProcessing = false;
        return checkoutSuccess( submitResponse.data.orderId, this.orderInfo );
      }

      // price > 0: stripe payment intent created
      if ( submitResponse.data.client_secret ) {        
        const paymentIntentSecretId = submitResponse.data.client_secret;
        const confirmation = await this.businessStripe.confirmCardPayment(paymentIntentSecretId);
        console.log('Payment intent confirmation: ', confirmation);
        if ( confirmation.error ) {
          this.paymentProcessing = false;
          return checkoutError( confirmation.error.message, 'Error', false );
        }
        return checkoutSuccess( submitResponse.data.orderId, this.orderInfo );
      } 

    } catch( e ) {
      this.paymentProcessing = false;
      console.error(e);
      if ( e.code === 'functions/deadline-exceeded' ) {
        return checkoutError( 'Please start the checkout process again.', 'Checkout session has expired.' );
      }
      if ( e.code === 'functions/failed-precondition' ) {
        return showPriceConfirmModal( () => this.handleSubmit( ev, e.details.total ) );
      }
      return checkoutError( e.message );      
    }
  }  
  

  render() {
    if ( checkout.checkoutStartLoading || checkout.checkoutCancelLoading || !checkout.checkoutId ) {
      return ( <tambr-loading-spinner /> );
    }

    return (
      <Host id='tambr-checkout-form' class='tambr-modal-window checkout-window'>

        <div class='tambr-modal-header'>
          <tambr-icon type='close' class='close-button' onClick={ () => closeCheckout() } />
        </div>

        <div class='tambr-modal-title'>
          <div>Checkout</div>
          <div class='timer-wrapper'>
            <div class='timer-label'>Expires in</div>
            <div class='timer'>{ this.showTimer() }</div>               
          </div>
        </div>

        { this.renderGreeting() }

        <div class='tambr-modal-body'>
          <tambr-checkout-order-info orderInfo={ this.orderInfo } />

          { this.renderAddOnsSection() }

          <tambr-order-fields />

          { this.renderNotesSection() }
          <tambr-checkout-order-price priceBreakdown={ getPriceBreakdown().priceBreakdown } />
        </div>

        { this.renderPaymentMethodSection() }

        <div class='tambr-action-buttons'>
          { this.renderBusinessTermsCheckbox() }
          <button type='button' 
            class={ 'tambr-button' + (this.isSubmitLoading() ? ' loading' : '') }
            disabled={ this.isSubmitDisabled() }
            onClick={ ev => this.handleSubmit(ev) }>
              <div class='tambr-button-loading'><div>Loading...</div></div>
              <span>Confirm and Pay</span>
          </button>
        </div>
        
        { this.renderPaymentProcessingSpinner() }
      </Host>        
    );    
  }

  isSubmitDisabled() { 
    return !this.paymentMethod 
      || ( settings.businessSettings?.checkoutSettings?.termsOfServiceRequired && !this.termsConfirmed )
      || this.paymentMethodLoading 
      || this.paymentProcessing; }

  isSubmitLoading() { return this.paymentMethodLoading || this.paymentProcessing; }

  renderGreeting() {
    return (
      <div class='tambr-modal-logout'>
        <div>Hi, {customer.platformCustomer.firstName}</div>
        <div class='tambr-modal-logout-link' onClick={ () => checkoutLogout() }>Log out</div>        
      </div>
    );
  }

  renderAddOnsSection() {
    if ( !settings.businessSettings?.checkoutSettings?.addOnsEnabled ) return;
    if ( !settings.businessSettings?.availableAddOns ) return;
    return (
      <div class='order-details'>
        <div class='order-details-row multi-line'>
          <div class='order-details-label'>Add-ons</div>
          <div class='order-details-value'>
            { selection.addOns.length 
              ? <div class='add-ons-value-wrapper'>
                  <div class='add-ons-value'>{ selection.addOns.map( addOn => this.renderAddOnChip( addOn ) ) }</div>
                  <tambr-icon type='edit' class='add-ons-edit-button' onClick={ () => openAddOnsModal() } />
                </div>
              : <div class='stroked-button' onClick={ () => openAddOnsModal() }>Rent or purchase equipment</div>
            }
          </div>
        </div>        
      </div>
    );
  }

  renderNotesSection() {
    if ( !settings.businessSettings?.checkoutSettings?.noteEnabled ) return;
    return (
      <div class='order-details'>
        <div class='order-details-row multi-line'>
          <div class='order-details-label'>Notes</div>
          <div class='order-details-value'>
            { selection.note 
              ? <div class='add-ons-value-wrapper'>
                  <div class='add-ons-value notes-text'>{ selection.note }</div>
                  <tambr-icon type='edit' class='add-ons-edit-button' onClick={ () => openCheckoutNoteModal() } />
                </div>
              : <div class='stroked-button' onClick={ () => openCheckoutNoteModal() }>Add comments or requests</div>
            }
          </div>
        </div>        
      </div>
    );
  }

  renderAddOnChip( addOn: OrderAddOn ) {
    return (
      <div class='add-on-chip'>
        <span>{ addOn.quantity > 1 ? addOn.quantity + ' × ' : '' }{ addOn.title }</span>
        <tambr-icon type='close-circle' onClick={ () => this.deselectAddOn(addOn.id) } />
      </div>
    );
  }

  renderPaymentMethodSection() {
    return (
      <div id='payment-method-wrapper'>
        <div class='payment-method'>
          <div class='payment-method-label'>Pay with</div>
          <div class='payment-method-value'>
            { this.paymentMethod
              ? [
                  <tambr-payment-method paymentMethod={ this.paymentMethod } />,
                  <span class='stroked-button' onClick={ () => this.openPaymentMethodSelectDialog() }>Update</span>
                ]
              : <div onClick={ () => this.openPaymentMethodAddDialog() }>
                  <span class='add-payment-method-button'>+ Add payment method</span>
                </div>
            }
          </div>
        </div>
      </div>
    );
  }

  renderBusinessTermsCheckbox() {
    if ( !settings.businessSettings?.checkoutSettings?.termsOfServiceRequired ) return;
    if ( !settings.businessSettings?.checkoutSettings?.termsOfService ) return;
    return (
      <div class='terms-checkbox'>
        <input type='checkbox' id='checkbox' name='checkbox' checked={ this.termsConfirmed } 
          onInput={ (event) => this.handleTermsConfirm(event)} />

        <label htmlFor='checkbox'>
          I agree with { settings.businessSettings.businessName }'s <a target='_blank' href={ settings.businessSettings?.checkoutSettings?.termsOfService }>Terms of Service</a>.
        </label>
      </div>
    );
  }

  renderPaymentProcessingSpinner() {
    if ( this.paymentProcessing ) {
      return (
        <div id='payment-processing'>
          <tambr-loading-spinner />
        </div>
      )
    }
  }

}